// @flow
/* eslint flowtype/generic-spacing: 0 */
import { List, Map, OrderedMap, OrderedSet, Record } from "immutable"
import _ from "lodash"
import fp from "lodash/fp"
import * as R from "ramda"
import moment from "moment"
import { createSelector } from "reselect"
import type { InputSelector } from "reselect"
import Booking from "@kiwicom/frontend-app/scripts/containers/mutable/Booking"
import type Passenger from "@kiwicom/frontend-app/scripts/containers/mutable/Passenger"
import BookingPendingRefund from "@kiwicom/frontend-app/scripts/containers/mutable/BookingPendingRefund"
import { PASSENGER_CATEGORIES } from "@kiwicom/frontend-app/scripts/containers/mutable/Passenger"

import PendingRequest from "common/records/PendingRequest"
import type Journey from "common/records/Journey"
import Flight from "common/records/Flight"
import { isGuaranteeEnabledSelector } from "common/services/branding/services"
// eslint-disable-next-line kiwicom/prefer-nitro-translate-function
import getTranslateSelector from "common/services/intl/translateSelector"
import type { RefundMethods } from "mmb/records/bookingInput"
import type { BookingStateInst } from "mmb/scenes/AccountBooking/components/Booking/services/types"
import { SERVICES, SERVICES_STATUSES } from "mmb/consts/additionalServices"
import { ACTIONS, BOOKING_STATUSES } from "mmb/consts/booking"
import { extractSeatmapPrices } from "../components/BookingSeating/components/BookingSeatmap/BookingSeatmap.helpers"
import { BAGS_TYPES } from "./bags/tools"
import type { Baggage } from "../../../../../common/types/baggageTypes"

// NOTE all these will be renamed after merge of ScheduleChanges

/** gets booking state */
export const bookingStateSelector = (state: any): BookingStateInst => state.booking

/** gets booking ID */
export const bidSelector = (state: any): number => Number(state.params.bookingId)

/** gets booking */
export const bookingDataSelector: InputSelector<any, void, Booking> = fp.compose(
  fp.get("data"),
  bookingStateSelector,
)

/** gets flights history */
export const flightsHistorySelector: InputSelector<any, void, List<Journey>> = createSelector(
  bookingDataSelector,
  booking => booking.flightsHistory,
)

/** gets passengers */
export const bookingPassengersSelector: InputSelector<any, void, List<Passenger>> = fp.compose(
  fp.get("passengers"),
  bookingDataSelector,
)

/** gets passenger by the ID from the booking's passengers */
export const currentBookingSpecificPassengerSelector = (state: any, pid: number): Passenger =>
  // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
  _.find(bookingPassengersSelector(state), pax => pax.id === pid)

/** gets available refund methods for whole booking ( not pending refunds ) */
export const refundMethodsSelector: InputSelector<any, void, RefundMethods> = createSelector(
  bookingDataSelector,
  data => data.refundMethods,
)

/** gets pending refund */
export const pendingRefundSelector: InputSelector<any, void, BookingPendingRefund> = createSelector(
  bookingDataSelector,
  data => data.pendingRefund,
)

/** gets pending requests */
export const pendingRequestsSelector: InputSelector<
  any,
  void,
  OrderedMap<PendingRequest>,
> = createSelector(
  bookingDataSelector,
  data => data.pendingRequests,
)

/**
 * gets pending additional services from booking
 * TODO: type AdditionalService
 */
export const bookingAdditionalServicesSelector: InputSelector<
  any,
  void,
  // eslint-disable-next-line flowtype/no-weak-types
  OrderedSet<Record<Object>>,
> = createSelector(
  bookingDataSelector,
  data => data.additionalServices,
)

/** determine if pending refunds modal should be open */
export const shouldOpenPendingRefundSelector: InputSelector<any, void, boolean> = createSelector(
  [pendingRefundSelector, pendingRequestsSelector],
  (pendingRefund, pendingRequests) => {
    const hasPendingRefund = Boolean(pendingRefund)
    const hasPendingRequests = pendingRequests.size > 0

    return hasPendingRefund || (hasPendingRequests && hasPendingRefund)
  },
)

export const priceChangeAdditionalServiceSelector: InputSelector<
  any,
  void,
  boolean,
> = createSelector(
  bookingAdditionalServicesSelector,
  additionalServices => additionalServices.find(s => s.category === SERVICES.PRICE_CHANGE),
)

const toMaybe = (val: any) => (!_.isUndefined(val) && !_.isNull(val) ? val : Map())

/**
 * get schedule change
 * TODO: type schedule change object returned from mapperScheduleChange.js
 */
export const currentBookingScheduleChangeSelector: InputSelector<
  any,
  void,
  // eslint-disable-next-line flowtype/no-weak-types
  Map<Object>,
  // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
> = fp.compose(
  toMaybe,
  val => (typeof val === "string" ? toMaybe(null) : val),
  fp.get("scheduleChange"),
  bookingDataSelector,
)

/**
 * !!! identical to bookingAdditionalServicesSelector
 */
export const currentBookingAdditionalServicesSelector: InputSelector<
  any,
  any,
  // eslint-disable-next-line flowtype/no-weak-types
  OrderedSet<Record<Object>>,
> = createSelector(
  bookingDataSelector,
  booking => booking.additionalServices,
)

/** get the journey */
export const currentBookingJourneySelector: InputSelector<any, void, Journey> = createSelector(
  [bookingDataSelector, currentBookingScheduleChangeSelector],
  (booking, scheduleChanges) => scheduleChanges.get("currentJourney") || booking.journey,
)

/** get the journey with hidden flights included */
export const currentBookingJourneyWithHiddenSelector: InputSelector<
  any,
  any,
  Journey,
> = createSelector(
  [bookingDataSelector, currentBookingScheduleChangeSelector],
  (booking, scheduleChanges) => scheduleChanges.get("currentJourney") || booking.journeyWithHidden,
)

/** get the flights from the journey (currently includes ground transport) */
export const bookingFlightsSelector: InputSelector<any, any, List<Flight>> = createSelector(
  currentBookingJourneySelector,
  journey => journey.getFlights(),
)

/** get the flights ids from the journey (currently includes ground transport) */
export const bookingFlightIdsSelector: InputSelector<any, any, List<string>> = createSelector(
  currentBookingJourneySelector,
  journey => journey.getFlightsIds(),
)

/**
 * !!! identical to bookingFlightsSelector
 */
export const currentBookingFlightsSelector: InputSelector<any, void, List<Flight>> = createSelector(
  currentBookingJourneySelector,
  journey => journey.getFlights(),
)

/**
 * returns number of hours. This number represents time until departure
 * before which changes to documents can be requested.
 */
export const canChangeDocumentsSelector: InputSelector<any, void, number> = createSelector(
  bookingDataSelector,
  booking => booking.config.allowedToChange.documents,
)

/** returns first flight in the journey (currently includes ground transport) */
export const currentBookingFirstFlightSelector: InputSelector<any, void, Flight> = createSelector(
  currentBookingJourneySelector,
  journey => journey.getFlights().first(),
)

// ANCILLARIES
export const lowestSeatingPriceSelector: $FlowFixMe = (state): ?number => {
  const { seatmaps } = state.booking.data

  if (seatmaps) {
    const allPrices = Object.values(seatmaps).reduce(
      // $FlowFixMe
      (acc, map) => [...acc, ...extractSeatmapPrices(map.seatmap)],
      [],
    )

    return _.min(allPrices)
  }

  return null
}

// SCHEDULE CHANGES
// ---

const scheduleChangesSelectorFactory: $FlowFixMe = propName =>
  createSelector(
    currentBookingScheduleChangeSelector,
    // toMaybe used so we can safely do operations on the incoming item even if it's null
    // $FlowFixMe: error revealed by updating Flow and lodash libdef, please fix
    fp.compose(
      toMaybe,
      c => c.get(propName),
    ),
  )

export const scheduleChangeAffectedFlightsSelector: $FlowFixMe = scheduleChangesSelectorFactory(
  "affected",
)
export const scheduleChangeFlightChangesSelector: $FlowFixMe = scheduleChangesSelectorFactory(
  "changes",
)
export const scheduleChangeActionSelector: $FlowFixMe = scheduleChangesSelectorFactory("action")
export const scheduleChangeFlightsCheckedSelector: $FlowFixMe = scheduleChangesSelectorFactory(
  "flightsChecked",
)
export const scheduleChangesisViewAlernativesSelector: $FlowFixMe = scheduleChangesSelectorFactory(
  "isViewAlternatives",
)
export const scheduleChangeAlternativesSelector: $FlowFixMe = scheduleChangesSelectorFactory(
  "alternatives",
)
export const scheduleChangeRefundSelector: $FlowFixMe = scheduleChangesSelectorFactory("refund")
export const scheduleChangesContactCSSelector: $FlowFixMe = scheduleChangesSelectorFactory(
  "contactCS",
)
export const hasConvenientAlternativeSelector: $FlowFixMe = scheduleChangesSelectorFactory(
  "hasConvenientAlternative",
)
export const scheduleChangeValidUntilSelector: $FlowFixMe = createSelector(
  scheduleChangesSelectorFactory("validUntil"),
  validUntil => (Map.isMap(validUntil) ? null : validUntil),
)

export const scheduleChangeIsGuaranteeValidSelector: $FlowFixMe = createSelector(
  scheduleChangeValidUntilSelector,
  validUntil => validUntil && moment.duration(validUntil.diff()).asMilliseconds() > 0,
)

export const scheduleChangesIsGuaranteeSelector: $FlowFixMe = createSelector(
  [currentBookingScheduleChangeSelector, isGuaranteeEnabledSelector],
  (c, isGuaranteeEnabledByBranding) => c.get("isGuaranteeCase") && isGuaranteeEnabledByBranding,
)

export const getMMBPriorityBoardingPassengersSelector: $FlowFixMe = createSelector(
  bookingStateSelector,
  booking => {
    const flights = booking.data.journey.getFlights()

    return flights.reduce((prev, flight) => {
      const flightId = flight.get("id")
      const passengersWithPBOrderedInfo = booking.data.passengers.map(passenger => ({
        ...passenger,
        hasOrderedPB: Boolean(
          passenger.priorityBoarding &&
            passenger.priorityBoarding[flightId] &&
            passenger.priorityBoarding[flightId].is_ordered,
        ),
      }))

      return {
        ...prev,
        [flightId]: passengersWithPBOrderedInfo,
      }
    }, {})
  },
)

/*
TODO simplify this to only decide if the booking is in progresss or not - there should be a global selector for this
We dont need to know if it's kiwi decides or paxdecides as this is returned by BE in the action paramter - KIWI/PAX
*/
export const scheduleChangesCaseSelector: $FlowFixMe = createSelector(
  currentBookingJourneySelector,
  journey => {
    const firstFlightDepartureTime = journey.trips
      .first()
      .parts.first()
      .departure.getIn(["when", "utc"])

    const isInProgress = firstFlightDepartureTime.isBefore()

    if (isInProgress) {
      return "inProgress"
    }

    // more than 48 before departure
    if (moment.duration(firstFlightDepartureTime.diff()).asHours() > 48) {
      return "kiwiDecides"
    }
    // less than 48 before departure
    if (moment.duration(firstFlightDepartureTime.diff()).asHours() < 48) {
      return "paxDecides"
    }

    return null
  },
)

// END OF SCHEDULE CHANGES
// ---

const translateSelector: $FlowFixMe = state => getTranslateSelector(state)

export const successMessageSelector: $FlowFixMe = createSelector(
  [bookingStateSelector, translateSelector],
  (state, translate) => {
    if (state.success) {
      switch (state.success.action) {
        case ACTIONS.CANCEL_BOOKING:
          return translate("account.booking_cancel_success")
        case ACTIONS.REQUEST_FAST_REFUND:
          return [translate("account.booking_fast_refund_success")].join(" ")
      }
    }
    return null
  },
)

export const bookingChangesSelector: $FlowFixMe = createSelector(
  bookingDataSelector,
  booking => booking.bookingChanges,
)

export const hasStrollerToRecheckSelector: $FlowFixMe = createSelector(
  bookingDataSelector,
  booking => {
    return (
      booking.infantsConditions.trolley &&
      booking.passengers.filter(p => p.category === PASSENGER_CATEGORIES.infant).length > 0
    )
  },
)

const getHoldBagsCount = (bags: Array<Baggage>): number =>
  R.compose(
    R.length,
    R.filter(bag => bag.bag.category === BAGS_TYPES.HOLD_BAG),
    R.ifElse(R.isEmpty, R.always([]), R.head),
    R.values,
    R.groupBy(bag => bag.flightId),
  )(bags)

export const hasBaggageOrServicesSelector: $FlowFixMe = createSelector(
  bookingDataSelector,
  booking => {
    const hasHoldBags = getHoldBagsCount(booking.baggages) > 0
    const hasAdditionalEquipment =
      booking.additionalServices.filter(
        service =>
          (service.category === SERVICES.PETS ||
            service.category === SERVICES.SPORTS_EQUIPMENT ||
            service.category === SERVICES.MUSICAL_EQUIPMENT) &&
          service.status === SERVICES_STATUSES.CONFIRMED,
      ).size > 0

    return hasHoldBags || hasAdditionalEquipment
  },
)

export const hasBaggageToRecheckSelector: $FlowFixMe = createSelector(
  [hasBaggageOrServicesSelector, hasStrollerToRecheckSelector],
  (hasBaggageOrServicesToRecheck, hasStroller) => {
    return hasBaggageOrServicesToRecheck || hasStroller
  },
)

export const showBaggageRecheckNoteSelector: $FlowFixMe = createSelector(
  [hasBaggageOrServicesSelector, currentBookingJourneySelector],
  (hasBaggage, journey) => journey.hasBagsRecheck() && journey.endsInFuture() && hasBaggage,
)

export const showStrollerRecheckNoteSelector: $FlowFixMe = createSelector(
  [hasStrollerToRecheckSelector, currentBookingJourneySelector],
  (hasStroller, journey) => journey.hasBagsRecheck() && journey.endsInFuture() && hasStroller,
)

export const shouldShowTrustPilotInviteSelector: $FlowFixMe = createSelector(
  bookingDataSelector,
  state => state.splitster,
  (booking, splitster) => {
    const { journey } = booking
    const isSimpleJourney =
      (journey.isOneWay() && journey.getFlights().size <= 2) ||
      (journey.isReturn() && journey.getFlights().size <= 4)
    return isSimpleJourney && splitster.get("TRUSTPILOT_INVITE").value === "show"
  },
)

export const shotEmergencyAirlineCodesSelector: $FlowFixMe = createSelector(
  [currentBookingScheduleChangeSelector, currentBookingFlightsSelector],
  (scheduleChange, flights) => {
    const emergencyAirlines = scheduleChange.get("emergencyAirlines")
    if (emergencyAirlines && !emergencyAirlines.isEmpty()) {
      return flights
        .reduce((acc, flight: Flight) => {
          const airlineCode = flight.getIn(["airline", "code"])
          if (emergencyAirlines.includes(airlineCode)) {
            return acc.push(airlineCode)
          }
          return acc
        }, List.of())
        .toSet()
        .toList()
    }
    return List.of()
  },
)

export const shotEmergencyAirlineNamesSelector: $FlowFixMe = createSelector(
  [
    currentBookingScheduleChangeSelector,
    currentBookingFlightsSelector,
    shotEmergencyAirlineCodesSelector,
  ],
  (scheduleChange, flights, emergencyAirlineCodes) =>
    emergencyAirlineCodes.isEmpty()
      ? emergencyAirlineCodes
      : flights
          .reduce((acc, flight) => {
            const airline = flight.get("airline")
            if (emergencyAirlineCodes.includes(airline.get("code"))) {
              return acc.push(airline.get("name"))
            }
            return acc
          }, List.of())
          .toSet()
          .toList(),
)

export const alternativelyHandledEmergencySelector: $FlowFixMe = createSelector(
  [shotEmergencyAirlineCodesSelector],
  shotEmergencyAirlineCodes => shotEmergencyAirlineCodes.toArray().includes("ZI"),
)

export const hasJourneyDepartedSelector: $FlowFixMe = createSelector(
  bookingDataSelector,
  booking =>
    booking.journey
      .getDeparture()
      .getIn(["when", "utc"])
      .isBefore(),
)

export const calculatorValidUntilSelector: $FlowFixMe = createSelector(
  bookingDataSelector,
  booking => booking.refundOptions.validUntil,
)

export const allowedToChangeAssistedRefundSelector: $FlowFixMe = createSelector(
  bookingDataSelector,
  booking => booking.config.allowedToChange.assistedRefund,
)

export const isBeforeAssistedRefundDeadlineSelector: $FlowFixMe = createSelector(
  [currentBookingJourneySelector, allowedToChangeAssistedRefundSelector],
  (journey, allowedToChangeAssistedRefund) => {
    const firstFlightDepartureTime = journey.trips
      .first()
      .parts.first()
      .departure.getIn(["when", "utc"])
    return (
      moment.duration(firstFlightDepartureTime.diff()).asHours() > allowedToChangeAssistedRefund
    )
  },
)

// $FlowFixMe: error revealed by updating Flow and Ramda libdef, please fix
export const checkInSelector = R.compose(
  R.prop("checkIn"),
  bookingDataSelector,
)

export const isUsSanctionsRestrictedSelector: $FlowFixMe = createSelector(
  [bookingDataSelector],
  booking => {
    const hasCubaFlight = booking.journey
      .getFlights()
      .some(
        flight =>
          flight.getIn(["departure", "where", "country"]) === "CU" ||
          flight.getIn(["arrival", "where", "country"]) === "CU",
      )

    const hasCubaPassenger = booking.passengers.some(x => x.nationality === "cu")

    return hasCubaFlight || hasCubaPassenger
  },
)

export const shouldShowBookingInvoiceSelector: $FlowFixMe = createSelector(
  [bookingDataSelector],
  booking => {
    const { CONFIRMED, REFUNDED, REFUNDING } = BOOKING_STATUSES
    return [CONFIRMED, REFUNDED, REFUNDING].includes(booking.status)
  },
)
